import React, { useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  alpha,
  useTheme
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  AutoFixHigh as ToolsIcon,
  PhotoSizeSelectLarge as UpscaleIcon,
  PanTool as HandIcon,
  Download as DownloadIcon,
  Face as FaceIcon
} from '@mui/icons-material'
import { useLanguage } from '../../contexts/LanguageContext'
import apiClient from '../../api/client'
import ImageSourceSelector, { ImageSource } from './ImageSourceSelector'
import ImageComparisonSlider from './ImageComparisonSlider'

interface AdvancedToolsProps {
  imageUrl: string | null
  projectName: string
  onProcessed: (newUrl: string) => void
}

const AdvancedTools: React.FC<AdvancedToolsProps> = ({
  imageUrl,
  projectName,
  onProcessed
}) => {
  const { t } = useLanguage()
  const theme = useTheme()

  // Shared image source state
  const [imageSource, setImageSource] = useState<ImageSource>('last_generated')
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  // Upscaling state
  const [upscaleScale, setUpscaleScale] = useState<2 | 4>(2)
  const [faceEnhance, setFaceEnhance] = useState(false)
  const [upscaling, setUpscaling] = useState(false)
  const [upscaleError, setUpscaleError] = useState<string | null>(null)
  const [upscaleSuccess, setUpscaleSuccess] = useState(false)
  const [upscaledImageUrl, setUpscaledImageUrl] = useState<string | null>(null)

  // Hand fix state
  const [handFixModel, setHandFixModel] = useState<'strawberry' | 'kiwi'>('strawberry')
  const [handFixing, setHandFixing] = useState(false)
  const [handFixError, setHandFixError] = useState<string | null>(null)
  const [handFixSuccess, setHandFixSuccess] = useState(false)
  const [handFixedImageUrl, setHandFixedImageUrl] = useState<string | null>(null)

  // Make real state
  const [makeRealModel, setMakeRealModel] = useState<'strawberry' | 'kiwi'>('strawberry')
  const [makingReal, setMakingReal] = useState(false)
  const [makeRealError, setMakeRealError] = useState<string | null>(null)
  const [makeRealSuccess, setMakeRealSuccess] = useState(false)
  const [makeRealImageUrl, setMakeRealImageUrl] = useState<string | null>(null)
  const [makeRealOriginalUrl, setMakeRealOriginalUrl] = useState<string | null>(null)

  const handleUpscale = async () => {
    // Validate source
    const effectiveImageUrl = imageSource === 'last_generated' ? imageUrl : selectedImageUrl

    if (!effectiveImageUrl) {
      setUpscaleError(t('easyWay.advanced.upscale.noImage'))
      return
    }

    try {
      setUpscaling(true)
      setUpscaleError(null)
      setUpscaleSuccess(false)

      // Build FormData
      const formData = new FormData()

      // If user uploaded a file, send the file itself
      if (imageSource === 'upload' && selectedFile) {
        formData.append('image_file', selectedFile)
      } else {
        // Otherwise send the URL (last_generated or explorer)
        formData.append('image_url', effectiveImageUrl)
      }

      formData.append('scale', upscaleScale.toString())
      formData.append('face_enhance', faceEnhance.toString())
      formData.append('project_name', projectName)

      const response = await apiClient.post('/easy_way/upscale', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      if (response.data.success) {
        setUpscaleSuccess(true)
        setUpscaledImageUrl(response.data.upscaled_url)
        onProcessed(response.data.upscaled_url)

        // Auto-hide success message after 5s
        setTimeout(() => setUpscaleSuccess(false), 5000)
      } else {
        setUpscaleError(response.data.message || t('easyWay.advanced.error'))
      }
    } catch (error: any) {
      console.error('Upscale error:', error)
      setUpscaleError(
        error.response?.data?.message || error.message || t('easyWay.advanced.error')
      )
    } finally {
      setUpscaling(false)
    }
  }

  const handleHandFix = async () => {
    const effectiveImageUrl = imageSource === 'last_generated' ? imageUrl : selectedImageUrl

    if (!effectiveImageUrl) {
      setHandFixError(t('easyWay.advanced.upscale.noImage'))
      return
    }

    try {
      setHandFixing(true)
      setHandFixError(null)
      setHandFixSuccess(false)

      // Build FormData
      const formData = new FormData()

      // If user uploaded a file, send the file itself
      if (imageSource === 'upload' && selectedFile) {
        formData.append('image_file', selectedFile)
      } else {
        // Otherwise send the URL (last_generated or explorer)
        formData.append('image_url', effectiveImageUrl)
      }

      formData.append('model', handFixModel)
      formData.append('project_name', projectName)

      const response = await apiClient.post('/easy_way/hand_fix', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      if (response.data.success) {
        setHandFixSuccess(true)
        setHandFixedImageUrl(response.data.fixed_url)
        onProcessed(response.data.fixed_url)

        // Auto-hide success message after 5s
        setTimeout(() => setHandFixSuccess(false), 5000)
      } else {
        setHandFixError(response.data.message || t('easyWay.advanced.error'))
      }
    } catch (error: any) {
      console.error('Hand fix error:', error)
      setHandFixError(
        error.response?.data?.message || error.message || t('easyWay.advanced.error')
      )
    } finally {
      setHandFixing(false)
    }
  }

  const handleMakeReal = async () => {
    const effectiveImageUrl = imageSource === 'last_generated' ? imageUrl : selectedImageUrl

    if (!effectiveImageUrl) {
      setMakeRealError(t('easyWay.advanced.upscale.noImage'))
      return
    }

    try {
      setMakingReal(true)
      setMakeRealError(null)
      setMakeRealSuccess(false)

      // Build FormData
      const formData = new FormData()

      // If user uploaded a file, send the file itself
      if (imageSource === 'upload' && selectedFile) {
        formData.append('image_file', selectedFile)
      } else {
        // Otherwise send the URL (last_generated or explorer)
        formData.append('image_url', effectiveImageUrl)
      }

      formData.append('model', makeRealModel)
      formData.append('project_name', projectName)

      const response = await apiClient.post('/easy_way/make_real', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      if (response.data.success) {
        setMakeRealSuccess(true)
        setMakeRealImageUrl(response.data.result_url)
        setMakeRealOriginalUrl(effectiveImageUrl)  // Save original for comparison
        onProcessed(response.data.result_url)

        // Auto-hide success message after 5s
        setTimeout(() => setMakeRealSuccess(false), 5000)
      } else {
        setMakeRealError(response.data.message || t('easyWay.advanced.error'))
      }
    } catch (error: any) {
      console.error('Make real error:', error)
      setMakeRealError(
        error.response?.data?.message || error.message || t('easyWay.advanced.error')
      )
    } finally {
      setMakingReal(false)
    }
  }

  const effectiveImageUrl = imageSource === 'last_generated' ? imageUrl : selectedImageUrl
  const upscaleDisabled = upscaling || !effectiveImageUrl
  const handFixDisabled = handFixing || !effectiveImageUrl
  const makeRealDisabled = makingReal || !effectiveImageUrl

  const handleDownload = (url: string, filename: string) => {
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Accordion defaultExpanded={false}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.03),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08)
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ToolsIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {t('easyWay.advanced.title')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('easyWay.advanced.subtitle')}
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 3 }}>
          {/* Image Source Selector - shared across all tools */}
          <ImageSourceSelector
            selectedSource={imageSource}
            onSourceChange={setImageSource}
            selectedImageUrl={selectedImageUrl}
            onImageSelect={setSelectedImageUrl}
            onFileSelect={setSelectedFile}
            lastGeneratedUrl={imageUrl}
            disabled={upscaling || handFixing || makingReal}
          />

          {/* Upscaling Section */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <UpscaleIcon sx={{ color: theme.palette.info.main }} />
              <Typography variant="subtitle1" fontWeight={600}>
                {t('easyWay.advanced.upscale.title')}
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('easyWay.advanced.upscale.description')}
            </Typography>

            {/* Scale selector */}
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              {t('easyWay.advanced.upscale.scale')}
            </Typography>
            <RadioGroup
              value={upscaleScale}
              onChange={(e) => setUpscaleScale(parseInt(e.target.value) as 2 | 4)}
              sx={{ mb: 2 }}
            >
              <FormControlLabel
                value={2}
                control={<Radio />}
                label={t('easyWay.advanced.upscale.scale2x')}
                disabled={upscaling}
              />
              <FormControlLabel
                value={4}
                control={<Radio />}
                label={t('easyWay.advanced.upscale.scale4x')}
                disabled={upscaling}
              />
            </RadioGroup>

            {/* Face enhance checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={faceEnhance}
                  onChange={(e) => setFaceEnhance(e.target.checked)}
                  disabled={upscaling}
                />
              }
              label={t('easyWay.advanced.upscale.faceEnhance')}
              sx={{ mb: 2 }}
            />

            {/* Upscale button */}
            <Button
              variant="contained"
              color="info"
              onClick={handleUpscale}
              disabled={upscaleDisabled}
              startIcon={upscaling ? <CircularProgress size={20} /> : <UpscaleIcon />}
              fullWidth
              sx={{ mb: 2 }}
            >
              {upscaling
                ? t('easyWay.advanced.upscale.processing')
                : t('easyWay.advanced.upscale.apply')}
            </Button>

            {/* Upscale status */}
            {upscaleSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('easyWay.advanced.success')}
              </Alert>
            )}
            {upscaleError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUpscaleError(null)}>
                {upscaleError}
              </Alert>
            )}

            {/* Upscaled image preview */}
            {upscaledImageUrl && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  border: '2px solid',
                  borderColor: 'success.main',
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.success.main, 0.05),
                  position: 'relative'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" fontWeight={600} color="success.main">
                    {t('easyWay.advanced.result')}:
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleDownload(upscaledImageUrl, 'upscaled-image.png')}
                    sx={{ color: 'success.main' }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Box>
                <Box
                  component="img"
                  src={upscaledImageUrl}
                  alt="Upscaled result"
                  sx={{
                    width: '100%',
                    maxHeight: 400,
                    objectFit: 'contain',
                    borderRadius: 1,
                    backgroundColor: 'background.default'
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Hand Fix Section */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <HandIcon sx={{ color: theme.palette.warning.main }} />
              <Typography variant="subtitle1" fontWeight={600}>
                {t('easyWay.advanced.handFix.title')}
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('easyWay.advanced.handFix.description')}
            </Typography>

            {/* Model selector */}
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              {t('easyWay.advanced.handFix.model')}
            </Typography>
            <RadioGroup
              value={handFixModel}
              onChange={(e) => setHandFixModel(e.target.value as 'strawberry' | 'kiwi')}
              sx={{ mb: 2 }}
            >
              <FormControlLabel
                value="strawberry"
                control={<Radio />}
                label="strawberry"
                disabled={handFixDisabled || handFixing}
              />
              <FormControlLabel
                value="kiwi-r"
                control={<Radio />}
                label="kiwi-r"
                disabled={handFixDisabled || handFixing}
              />
            </RadioGroup>

            {/* Hand fix button */}
            <Button
              variant="contained"
              color="warning"
              onClick={handleHandFix}
              disabled={handFixDisabled || handFixing}
              startIcon={handFixing ? <CircularProgress size={20} /> : <HandIcon />}
              fullWidth
              sx={{ mb: 2 }}
            >
              {handFixing
                ? t('easyWay.advanced.handFix.processing')
                : t('easyWay.advanced.handFix.apply')}
            </Button>

            {/* Hand fix status */}
            {handFixSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('easyWay.advanced.success')}
              </Alert>
            )}
            {handFixError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setHandFixError(null)}>
                {handFixError}
              </Alert>
            )}

            {/* Hand fixed image preview */}
            {handFixedImageUrl && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  border: '2px solid',
                  borderColor: 'success.main',
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.success.main, 0.05),
                  position: 'relative'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" fontWeight={600} color="success.main">
                    {t('easyWay.advanced.result')}:
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleDownload(handFixedImageUrl, 'hand-fixed-image.png')}
                    sx={{ color: 'success.main' }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Box>
                <Box
                  component="img"
                  src={handFixedImageUrl}
                  alt="Hand fix result"
                  sx={{
                    width: '100%',
                    maxHeight: 400,
                    objectFit: 'contain',
                    borderRadius: 1,
                    backgroundColor: 'background.default'
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Make Real Section */}
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <FaceIcon sx={{ color: theme.palette.success.main }} />
              <Typography variant="subtitle1" fontWeight={600}>
                {t('easyWay.advanced.makeReal.title')}
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('easyWay.advanced.makeReal.description')}
            </Typography>

            {/* Model selector */}
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              {t('easyWay.advanced.makeReal.model')}
            </Typography>
            <RadioGroup
              value={makeRealModel}
              onChange={(e) => setMakeRealModel(e.target.value as 'strawberry' | 'kiwi')}
              sx={{ mb: 2 }}
            >
              <FormControlLabel
                value="strawberry"
                control={<Radio />}
                label="strawberry"
                disabled={makeRealDisabled || makingReal}
              />
              <FormControlLabel
                value="kiwi-r"
                control={<Radio />}
                label="kiwi-r"
                disabled={makeRealDisabled || makingReal}
              />
            </RadioGroup>

            {/* Make real button */}
            <Button
              variant="contained"
              color="success"
              onClick={handleMakeReal}
              disabled={makeRealDisabled || makingReal}
              startIcon={makingReal ? <CircularProgress size={20} /> : <FaceIcon />}
              fullWidth
              sx={{ mb: 2 }}
            >
              {makingReal
                ? t('easyWay.advanced.makeReal.processing')
                : t('easyWay.advanced.makeReal.apply')}
            </Button>

            {/* Make real status */}
            {makeRealSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('easyWay.advanced.success')}
              </Alert>
            )}
            {makeRealError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMakeRealError(null)}>
                {makeRealError}
              </Alert>
            )}

            {/* Make real image preview with comparison slider */}
            {makeRealImageUrl && makeRealOriginalUrl && (
              <ImageComparisonSlider
                beforeImage={makeRealOriginalUrl}
                afterImage={makeRealImageUrl}
                beforeLabel={t('easyWay.advanced.comparison.before')}
                afterLabel={t('easyWay.advanced.comparison.after')}
                onDownload={() => handleDownload(makeRealImageUrl, 'make-real-image.png')}
              />
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Paper>
  )
}

export default AdvancedTools
